/** @graphql cc **/
import React, { useContext, useRef, useCallback } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import gql from 'graphql-tag';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import {
	NativeCollabPresenceStateContainer,
	useNativeCollabPresenceState,
} from '@confluence/native-collab/entry-points/useNativeCollabPresenceState';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';

import {
	type PresenceParticipant,
	type PresenceSessionData,
	PresenceStoreContainer,
} from '../presence-store';
import type {
	CanvasCollaborators,
	PresenceContentMode,
	PresencePageContentType,
} from '../presenceTypes';
import { CURRENT_USER_DUMMY_SESSION_ID } from '../presenceUtils';
import { PagePresence } from '../page-presence/PagePresence';
import { useAnonymousAsset } from '../page-presence/useAnonymousAsset';

import type {
	ContentHeaderPresenceUserQuery as ContentHeaderPresenceUserQuery$data,
	ContentHeaderPresenceUserQueryVariables as ContentHeaderPresenceUserQuery$variables,
	ContentHeaderPresenceUserQuery_user_AtlassianUser,
} from './__types__/ContentHeaderPresenceUserQuery';
import type {
	ContentHeaderPresenceContentSettingsQuery as ContentHeaderPresenceContentSettingsQuery$data,
	ContentHeaderPresenceContentSettingsQueryVariables as ContentHeaderPresenceContentSettingsQuery$variables,
} from './__types__/ContentHeaderPresenceContentSettingsQuery';
import { CanvasPresence } from './CanvasPresence';

export type ContentHeaderPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType?: string | null;
	isViewMode?: boolean;
	analyticSource?: string;
};

const createDefaultParticipant = ({
	presenceId,
	activity,
	user,
	isGuest,
}: {
	presenceId: string;
	activity: PresenceActivity;
	user?: ContentHeaderPresenceUserQuery_user_AtlassianUser | null;
	isGuest: boolean;
}): PresenceParticipant | undefined => {
	if (!user) {
		return undefined;
	}

	const sessions: PresenceSessionData = {
		[CURRENT_USER_DUMMY_SESSION_ID]: {
			activity,
		},
	};

	const usablePresenceId = user?.id ?? presenceId;

	return {
		id: usablePresenceId,
		userId: user?.id ?? 'unidentified',
		presenceId: usablePresenceId,
		name: user?.displayName ?? '',
		src: user?.photos?.[0]?.value ?? '',
		isCurrentUser: true,
		isGuest,
		sessions,
	};
};

const UFO_SEGMENT_NAME = 'content-header-presence';

export const ContentHeaderPresenceComponent = ({
	collaborators,
	contentId,
	contentType,
	isViewMode,
	analyticSource,
}: ContentHeaderPresenceProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { cloudId } = useSessionData();
	const isGuest = useIsGuest();
	const spaceKey = useSpaceKey();

	const isEditPage = useIsEditorPage();

	const isLivePage = useIsCurrentPageLive();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const { fetchAnonymousAsset } = useAnonymousAsset();

	const isCanvasType = contentType === 'whiteboard' || contentType === 'database';

	const { data: userData, error: userQueryError } = useQuery<
		ContentHeaderPresenceUserQuery$data,
		ContentHeaderPresenceUserQuery$variables
	>(gql`
		query ContentHeaderPresenceUserQuery {
			user: confluence_atlassianUser(current: true) {
				id
				displayName
				photos {
					value
					isPrimary
				}
			}
		}
	`);

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();

	const presenceIdRef = useRef<string>(presenceId);

	let contentMode: PresenceContentMode = 'view';

	if (isLivePage) {
		contentMode = 'live';
	} else if (isEditPage) {
		contentMode = 'edit';
	}

	const activity: PresenceActivity =
		contentMode === 'edit' || (contentMode === 'live' && (isLiveEditMode || !isViewMode))
			? 'editor'
			: 'viewer';

	const { data: settingData, error: settingsQueryError } = useQuery<
		ContentHeaderPresenceContentSettingsQuery$data,
		ContentHeaderPresenceContentSettingsQuery$variables
	>(
		gql`
			query ContentHeaderPresenceContentSettingsQuery($cloudId: ID!, $spaceKey: String!) {
				confluence_teamPresenceContentSetting(cloudId: $cloudId, spaceKey: $spaceKey) {
					isEnabledOnContentView
				}
			}
		`,
		{
			variables: {
				cloudId,
				spaceKey: spaceKey ?? '',
			},
			skip: isCanvasType || contentMode !== 'view',
		},
	);

	const error = userQueryError || settingsQueryError;

	const isSettingEnabledOnView = useCallback(() => {
		return (
			isCanvasType ||
			settingData?.confluence_teamPresenceContentSetting?.isEnabledOnContentView === true
		);
	}, [isCanvasType, settingData]);

	const handleError = (err: Error) => {
		experienceTracker.fail({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			error: err,
		});

		markErrorAsHandled(err);

		return null;
	};

	if (!contentType) {
		return null;
	}

	if (error) {
		handleError(error);
	}

	if (contentMode === 'view' && !isSettingEnabledOnView()) {
		experienceTracker.abort({
			name: TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE,
			reason: `Real-time visibility is disabled on view mode`,
		});

		return null;
	}

	return (
		<PresenceStoreContainer
			participant={createDefaultParticipant({
				presenceId: presenceIdRef.current,
				activity,
				user: userData?.user,
				isGuest,
			})}
			fetchAnonymousAsset={fetchAnonymousAsset}
			onError={handleError}
		>
			{isCanvasType ? (
				<CanvasPresence
					collaborators={collaborators}
					contentId={contentId}
					contentType={contentType}
					analyticSource={analyticSource}
				/>
			) : (
				<PagePresence
					contentId={contentId}
					contentMode={contentMode}
					contentType={contentType as PresencePageContentType}
					activity={activity}
					analyticSource={analyticSource}
				/>
			)}
		</PresenceStoreContainer>
	);
};

export const ContentHeaderPresence = (props: ContentHeaderPresenceProps) => {
	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			<UFOSegment name={UFO_SEGMENT_NAME}>
				<NativeCollabPresenceStateContainer scope={props.contentId}>
					<ExperienceStart name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
					<ContentHeaderPresenceComponent {...props} />
				</NativeCollabPresenceStateContainer>
			</UFOSegment>
		</ErrorBoundary>
	);
};
